import React, { useEffect, useRef } from "react";
import { useLang } from "../i18n/LangContext";
import "./Screen2.css";

const employees = [
  { initials: "AM", name: "Anne Mäkinen", role: "Marketing Manager", color: "#f59e0b", arrival: "11:08", late: "02:08", badge: 3 },
  { initials: "SR", name: "Sanna Rantanen", role: "Sales Manager", color: "#8b5cf6", arrival: "10:38", late: "01:38", badge: 1 },
  { initials: "TK", name: "Timo Korhonen", role: "Sales Associate", color: "#2b6cee", arrival: "10:31", late: "01:31" },
  { initials: "JN", name: "Janne Nurmi", role: "Head of Sales", color: "#16a34a", arrival: "10:28", late: "01:28" },
  { initials: "MH", name: "Mikko Hämäläinen", role: "UX/UI Designer", color: "#64748b", arrival: "09:34" },
];

const performers = [
  { initials: "TK", name: "Timo Korhonen", role: "Head of Technical Support", pct: 96, color: "#16a34a" },
  { initials: "JN", name: "Janne Nurmi", role: "Technical Support Specialist", pct: 71, color: "#2b6cee" },
  { initials: "AM", name: "Anne Mäkinen", role: "Sales Manager", pct: 77, color: "#16a34a" },
];

const Screen2: React.FC = () => {
  const { t } = useLang();
  const s = t.screens.s2;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 180 * dpr;
    canvas.height = 180 * dpr;
    ctx.scale(dpr, dpr);

    const data = [
      { value: 18, color: "#f59e0b" },
      { value: 11, color: "#ef4444" },
      { value: 8, color: "#16a34a" },
      { value: 10, color: "#2b6cee" },
    ];
    const total = data.reduce((sum, d) => sum + d.value, 0);
    let startAngle = -Math.PI / 2;
    data.forEach((seg) => {
      const sweep = (seg.value / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(90, 90, 72, startAngle, startAngle + sweep);
      ctx.lineWidth = 22;
      ctx.strokeStyle = seg.color;
      ctx.lineCap = "round";
      ctx.stroke();
      startAngle += sweep + 0.04;
    });
  }, []);

  return (
    <div className="s2-page">
      <nav className="topnav">
        <div className="logo">Worklly</div>
        <div className="tabs">
          <div className="tab active">{s.dashboard}</div>
          <div className="tab">{s.employees}</div>
          <div className="tab">{s.schedules}</div>
          <div className="tab">{s.reports}</div>
        </div>
        <div className="right">
          <div className="icon-btn">&#128276;</div>
          <div className="icon-btn">&#9776;</div>
          <div className="icon-btn">&#9881;</div>
          <div className="avatar">AM</div>
        </div>
      </nav>

      <div className="s2-layout">
        <aside className="s2-sidebar">
          <div className="date-block">
            <div className="day">{s.day}</div>
            <div className="meta">{s.dateMeta.split("\n").map((line, i) => <span key={i}>{line}<br /></span>)}</div>
          </div>

          <div className="donut-area">
            <div className="donut-wrap">
              <canvas ref={canvasRef} width={180} height={180} />
              <div className="donut-center">
                <div className="num">47</div>
                <div className="lbl">{s.employeesLabel}</div>
              </div>
            </div>
            <div className="stats-row">
              <div className="stat-item present"><div className="val">18</div><div className="label">{s.present}</div></div>
              <div className="stat-item absent"><div className="val">29</div><div className="label">{s.absent}</div></div>
              <div className="stat-item late"><div className="val">11</div><div className="label">{s.late}</div></div>
            </div>
          </div>

          <div className="devices">
            <div className="hdr"><h3>{s.devices}</h3><a href="#devices">{s.allDevices}</a></div>
            <div className="device-card">
              <div className="device-dot online" />
              <div className="device-info">
                <div className="id">F30962774 &#x1F7E2;</div>
                <div className="detail">Last sync: 21.02.2025 11:45<br />Location: WORKLLY HQ</div>
              </div>
            </div>
            <div className="device-card">
              <div className="device-dot offline" />
              <div className="device-info">
                <div className="id">BD00E1CPA &#x1F534;</div>
                <div className="detail">Last sync: 21.02.2025 09:43<br />Location: WORKLLY HQ</div>
              </div>
            </div>
          </div>
        </aside>

        <div className="s2-main">
          <div className="main-header">
            <h2>{s.todayHeading}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="search-box"><span>&#128269;</span><input placeholder={s.search} /></div>
            </div>
          </div>

          <table className="emp-table">
            <thead>
              <tr>
                <th>{s.thEmployee}</th><th>{s.thFirstArrival}</th><th>{s.thLastDeparture}</th><th>{s.thLate}</th><th>{s.thEarlyDeparture}</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e, i) => (
                <tr key={i}>
                  <td>
                    <div className="emp-cell">
                      <div style={{ position: "relative" }}>
                        <div className="emp-avatar" style={{ background: e.color }}>{e.initials}</div>
                        {e.badge && <div className="badge-notif">{e.badge}</div>}
                      </div>
                      <div><div className="name">{e.name}</div><div className="role">{e.role}</div></div>
                    </div>
                  </td>
                  <td>{e.arrival}</td>
                  <td></td>
                  <td>{e.late && <span className="late-tag">{e.late}</span>}</td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <span>{s.showing}</span>
            <div className="page-btns">
              <div className="page-btn active">1</div>
              <div className="page-btn">2</div>
              <div className="page-btn">3</div>
              <div className="page-btn">4</div>
              <div className="page-btn">&larr;</div>
              <div className="page-btn">&rarr;</div>
            </div>
          </div>

          <div className="top-section">
            <h3>{s.topPerformers}</h3>
            <div className="top-grid">
              {performers.map((p, i) => (
                <div className="top-card" key={i}>
                  <div><div className="avatar-img">{p.initials}</div></div>
                  <div className="top-info"><div className="name">{p.name}</div><div className="role">{p.role}</div></div>
                  <div className="ring-score" style={{ background: `conic-gradient(${p.color} ${p.pct}%, #e5e7eb ${p.pct}%)` }}>
                    <div className="ring-inner" style={{ color: p.color }}>{p.pct}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen2;
